import {
  colorForProfileDetail as colorForRouteDetail,
  detailValueKey,
  formatProfileDetailValue as formatRouteDetailValue
} from '../profile/profile-model';
import type { ProfileColorMode, RouteFeature } from '../types';
import type { MapBounds } from './map-camera-controller';

const routePalette = ['#2dd4bf', '#60a5fa', '#f97316', '#a78bfa', '#f43f5e', '#22c55e', '#eab308'];

interface GeoJsonSource {
  setData(data: unknown): void;
}

interface RouteLayerRuntime {
  isStyleLoaded(): boolean;
  getStyle(): { layers?: Array<{ id: string; type?: string }> } | undefined;
  getLayer(id: string): unknown;
  getSource(id: string): GeoJsonSource | undefined;
  addSource(id: string, source: unknown): unknown;
  addLayer(layer: any, beforeId?: string): unknown;
  setLayoutProperty(id: string, property: string, value: unknown): unknown;
}

export interface RouteLayerState {
  routes: RouteFeature[];
  activeRouteNums: number[];
  profileColorMode: ProfileColorMode;
  textFont: string[];
}

export interface RouteCursorState {
  mapFeature: unknown;
  textFont: string[];
}

export interface RouteLayerController {
  apply(state: RouteLayerState): void;
  setCursor(cursor: RouteCursorState | null): void;
  dispose(): void;
}

type RegisterStyleGroup = (id: string, apply: () => void) => () => void;

function haversineKm(a: readonly [number, number, number?], b: readonly [number, number, number?]) {
  const dLat = (b[1] - a[1]) * Math.PI / 180;
  const dLon = (b[0] - a[0]) * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(a[1] * Math.PI / 180) * Math.cos(b[1] * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function computeKmMarkers(coords: RouteFeature['geometry']['coordinates']) {
  const markers: GeoJSON.Feature<GeoJSON.Point>[] = [];
  let cumDist = 0;
  let nextMark = 100;
  for (let i = 1; i < coords.length; i += 1) {
    const segLen = haversineKm(coords[i - 1], coords[i]);
    while (cumDist + segLen >= nextMark) {
      const frac = (nextMark - cumDist) / (segLen || 1);
      markers.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [
            coords[i - 1][0] + frac * (coords[i][0] - coords[i - 1][0]),
            coords[i - 1][1] + frac * (coords[i][1] - coords[i - 1][1])
          ]
        },
        properties: { label: String(nextMark) }
      });
      nextMark += 100;
    }
    cumDist += segLen;
  }
  return markers;
}

function routeLabelFeature(route: RouteFeature): GeoJSON.Feature<GeoJSON.Point> | null {
  const coords = route.geometry.coordinates;
  if (!coords.length) return null;
  const mid = coords[Math.floor(coords.length / 2)];
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [mid[0], mid[1]] },
    properties: {
      route_num: route.properties.route_num,
      label: route.properties.short_name || route.properties.name,
      color: route.properties.color || '#3b82f6'
    }
  };
}

function buildRouteDetailSegments(routes: RouteFeature[], mode: ProfileColorMode) {
  if (mode === 'elevation') return { type: 'FeatureCollection', features: [] } as GeoJSON.FeatureCollection;
  const features: GeoJSON.Feature<GeoJSON.LineString>[] = [];
  routes.forEach((route) => {
    const ranges = route.properties.details?.[mode] || [];
    ranges.forEach((range) => {
      let start = Math.max(0, Number(range[0]) || 0);
      const end = Math.min(route.geometry.coordinates.length - 1, Number(range[1]) || 0);
      if (end <= start && start > 0) start -= 1;
      const segmentCoords = route.geometry.coordinates.slice(start, end + 1);
      if (segmentCoords.length < 2) return;
      const value = range[2];
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: segmentCoords as GeoJSON.Position[] },
        properties: {
          route_num: route.properties.route_num,
          detail_value: detailValueKey(value),
          detail_label: formatRouteDetailValue(mode, value),
          detail_color: colorForRouteDetail(mode, value, route.properties.color),
          detail_is_tunnel: mode === 'road_environment' && detailValueKey(value).toLowerCase() === 'tunnel'
        }
      });
    });
  });
  return { type: 'FeatureCollection', features } as GeoJSON.FeatureCollection;
}

export function boundsForRoutes(routes: RouteFeature[]): MapBounds | null {
  const coords = routes.flatMap((route) => route.geometry.coordinates);
  if (!coords.length) return null;
  let west = Infinity, south = Infinity, east = -Infinity, north = -Infinity;
  coords.forEach(([lng, lat]) => {
    west = Math.min(west, lng);
    south = Math.min(south, lat);
    east = Math.max(east, lng);
    north = Math.max(north, lat);
  });
  return [[west, south], [east, north]];
}

export function createRouteLayerController(
  runtime: RouteLayerRuntime,
  registerStyleGroup: RegisterStyleGroup
): RouteLayerController {
  let latestState: RouteLayerState | null = null;
  let latestCursor: RouteCursorState | null = null;

  const controller: RouteLayerController = {
    apply(state) {
      latestState = state;
      if (!runtime.isStyleLoaded()) return;
      const { routes, activeRouteNums, profileColorMode, textFont } = state;
      routes.forEach((route, index) => {
        const id = `route-${route.properties.route_num}`;
        if (runtime.getSource(id)) {
          runtime.getSource(id)?.setData(route as GeoJSON.Feature);
        } else {
          runtime.addSource(id, { type: 'geojson', data: route as GeoJSON.Feature });
          runtime.addLayer({
            id: `${id}-line-bg`, type: 'line', source: id,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: { 'line-color': '#0f172a', 'line-width': 8, 'line-opacity': 0.36 }
          });
          runtime.addLayer({
            id: `${id}-line`, type: 'line', source: id,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': route.properties.color || routePalette[index % routePalette.length],
              'line-width': ['interpolate', ['linear'], ['zoom'], 4, 2, 10, 4, 14, 7],
              'line-opacity': 0.95
            }
          });
        }
        const visible = activeRouteNums.includes(route.properties.route_num) ? 'visible' : 'none';
        [`${id}-line-bg`, `${id}-line`].forEach((layerId) => {
          if (runtime.getLayer(layerId)) runtime.setLayoutProperty(layerId, 'visibility', visible);
        });
        const kmSource = `${id}-km`;
        const kmData = { type: 'FeatureCollection', features: computeKmMarkers(route.geometry.coordinates) } as GeoJSON.FeatureCollection;
        if (runtime.getSource(kmSource)) {
          runtime.getSource(kmSource)?.setData(kmData);
        } else {
          runtime.addSource(kmSource, { type: 'geojson', data: kmData });
          runtime.addLayer({
            id: `${id}-km-circles`, type: 'circle', source: kmSource,
            paint: {
              'circle-radius': ['interpolate', ['linear'], ['zoom'], 3, 4, 10, 8],
              'circle-color': route.properties.color || routePalette[index % routePalette.length],
              'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff'
            }
          });
          runtime.addLayer({
            id: `${id}-km-labels`, type: 'symbol', source: kmSource,
            layout: {
              'text-field': ['get', 'label'], 'text-size': ['interpolate', ['linear'], ['zoom'], 3, 9, 10, 12],
              'text-allow-overlap': false, 'text-font': textFont
            },
            paint: { 'text-color': '#ffffff', 'text-halo-color': route.properties.color || routePalette[index % routePalette.length], 'text-halo-width': 1.5 }
          });
        }
        [`${id}-km-circles`, `${id}-km-labels`].forEach((layerId) => {
          if (runtime.getLayer(layerId)) runtime.setLayoutProperty(layerId, 'visibility', 'none');
        });
      });

      const selectedRoutes = routes.filter((route) => activeRouteNums.includes(route.properties.route_num));
      const routeLabels = {
        type: 'FeatureCollection', features: selectedRoutes.map(routeLabelFeature).filter(Boolean)
      } as GeoJSON.FeatureCollection;
      if (runtime.getSource('routes-labels-source')) runtime.getSource('routes-labels-source')?.setData(routeLabels);
      else {
        runtime.addSource('routes-labels-source', { type: 'geojson', data: routeLabels });
        runtime.addLayer({
          id: 'routes-labels', type: 'symbol', source: 'routes-labels-source',
          layout: { 'text-field': ['get', 'label'], 'text-size': 12, 'text-offset': [0, -1.5], 'text-anchor': 'bottom', 'text-allow-overlap': false, 'text-font': textFont },
          paint: { 'text-color': ['get', 'color'], 'text-halo-color': 'rgba(255,255,255,0.9)', 'text-halo-width': 2 }
        });
      }

      const markerData = { type: 'FeatureCollection', features: [] } as GeoJSON.FeatureCollection;
      if (runtime.getSource('routes-markers-source')) runtime.getSource('routes-markers-source')?.setData(markerData);
      else {
        runtime.addSource('routes-markers-source', { type: 'geojson', data: markerData });
        runtime.addLayer({
          id: 'routes-markers', type: 'symbol', source: 'routes-markers-source',
          layout: { 'text-field': ['get', 'label'], 'text-size': 14, 'text-offset': [0, -0.5], 'text-anchor': 'bottom', 'text-allow-overlap': false, 'text-font': textFont },
          paint: { 'text-color': '#ffffff', 'text-halo-color': '#000000', 'text-halo-width': 2 }
        });
        runtime.addLayer({
          id: 'routes-markers-dot', type: 'circle', source: 'routes-markers-source',
          paint: { 'circle-radius': 6, 'circle-color': '#ffffff', 'circle-stroke-width': 2, 'circle-stroke-color': '#000000' }
        });
      }

      const fuelPlanStops: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
      if (!runtime.getSource('routes-fuel-plan')) {
        runtime.addSource('routes-fuel-plan', { type: 'geojson', data: fuelPlanStops });
        runtime.addLayer({
          id: 'routes-fuel-plan-dot', type: 'circle', source: 'routes-fuel-plan',
          paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 5, 10, 9], 'circle-color': '#f59e0b', 'circle-stroke-width': 2, 'circle-stroke-color': '#111827' }
        });
        runtime.addLayer({
          id: 'routes-fuel-plan-labels', type: 'symbol', source: 'routes-fuel-plan',
          layout: { 'text-field': ['concat', ['get', 'label'], '\n', ['get', 'title']], 'text-size': 11, 'text-offset': [0, 1.4], 'text-anchor': 'top', 'text-allow-overlap': false, 'text-font': textFont },
          paint: { 'text-color': '#111827', 'text-halo-color': 'rgba(255,255,255,0.95)', 'text-halo-width': 2 }
        });
      }
      ['routes-fuel-plan-dot', 'routes-fuel-plan-labels'].forEach((layerId) => {
        if (runtime.getLayer(layerId)) runtime.setLayoutProperty(layerId, 'visibility', activeRouteNums.includes(3) ? 'visible' : 'none');
      });

      const detailData = buildRouteDetailSegments(selectedRoutes, profileColorMode);
      if (runtime.getSource('routes-detail-segments')) runtime.getSource('routes-detail-segments')?.setData(detailData);
      else {
        runtime.addSource('routes-detail-segments', { type: 'geojson', data: detailData });
        runtime.addLayer({
          id: 'routes-detail-segments-outline', type: 'line', source: 'routes-detail-segments',
          filter: ['!=', ['get', 'detail_is_tunnel'], true], layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#ffffff', 'line-width': ['interpolate', ['linear'], ['zoom'], 3, 5, 10, 8], 'line-opacity': 0.72 }
        });
        runtime.addLayer({
          id: 'routes-detail-segments-line', type: 'line', source: 'routes-detail-segments',
          filter: ['!=', ['get', 'detail_is_tunnel'], true], layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': ['get', 'detail_color'], 'line-width': ['interpolate', ['linear'], ['zoom'], 3, 3, 10, 6], 'line-opacity': 0.9 }
        });
        runtime.addLayer({
          id: 'routes-detail-segments-tunnel', type: 'line', source: 'routes-detail-segments',
          filter: ['==', ['get', 'detail_is_tunnel'], true], layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': ['get', 'detail_color'], 'line-width': ['interpolate', ['linear'], ['zoom'], 3, 2, 10, 4], 'line-opacity': 0.42, 'line-dasharray': [1.2, 1.2], 'line-blur': 0.6 }
        });
      }
      const detailsVisible = profileColorMode !== 'elevation' && selectedRoutes.length > 0 ? 'visible' : 'none';
      ['routes-detail-segments-outline', 'routes-detail-segments-line', 'routes-detail-segments-tunnel'].forEach((layerId) => {
        if (runtime.getLayer(layerId)) runtime.setLayoutProperty(layerId, 'visibility', detailsVisible);
      });
      routes.forEach((route) => {
        const id = `route-${route.properties.route_num}`;
        const visible = activeRouteNums.includes(route.properties.route_num) && profileColorMode === 'elevation' ? 'visible' : 'none';
        [`${id}-line-bg`, `${id}-line`].forEach((layerId) => {
          if (runtime.getLayer(layerId)) runtime.setLayoutProperty(layerId, 'visibility', visible);
        });
      });
    },
    setCursor(cursor) {
      latestCursor = cursor;
      if (!runtime.isStyleLoaded()) return;
      const sourceId = 'route-profile-cursor';
      const beforeSymbols = runtime.getStyle()?.layers?.find((layer) => layer.type === 'symbol')?.id;
      if (!runtime.getSource(sourceId)) runtime.addSource(sourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      if (!runtime.getLayer('route-profile-cursor-dot')) {
        runtime.addLayer({
          id: 'route-profile-cursor-dot', type: 'circle', source: sourceId,
          paint: { 'circle-radius': 8, 'circle-color': ['get', 'color'], 'circle-stroke-width': 3, 'circle-stroke-color': '#ffffff' }
        }, beforeSymbols);
      }
      if (!runtime.getLayer('route-profile-cursor-label')) {
        runtime.addLayer({
          id: 'route-profile-cursor-label', type: 'symbol', source: sourceId,
          layout: { 'text-field': ['get', 'label'], 'text-size': 12, 'text-offset': [0, -1.4], 'text-anchor': 'bottom', 'text-allow-overlap': true, 'text-font': cursor?.textFont || ['Noto Sans Regular'] },
          paint: { 'text-color': '#111827', 'text-halo-color': '#ffffff', 'text-halo-width': 2 }
        }, beforeSymbols);
      }
      runtime.getSource(sourceId)?.setData({ type: 'FeatureCollection', features: cursor ? [cursor.mapFeature] : [] });
    },
    dispose() { unregister(); latestState = null; latestCursor = null; }
  };

  const unregister = registerStyleGroup('routes', () => {
    if (latestState) controller.apply(latestState);
    if (latestCursor) controller.setCursor(latestCursor);
  });
  return controller;
}
